import React from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useFormState, useWatch } from 'react-hook-form'
import { ScrollRestore, PairingButton, FormSectionTitle } from 'src/Libs/flow/components'
import { getDeviceDefaultName } from '../../../parsers/device-parsers/get-device-default-name'
import { checkTrueRecursively } from '../../../utils/check-true-recursively'
import { logError } from '../../../utils/logger'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { HideDirtyStateProvider } from '../../providers'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField, TextField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

/**
 * Advanced settings for a heatpump device.
 */
export const Heatpump: React.FC = () => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { openHeatpumpPairingDialog, openConfirmationModal, openInstallationDeleteDialog } = useModals()

  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const fullWidth = !upSm

  const { control, getValues, setValue, reset } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({ control, name: 'heatpump' })

  const isDirty = checkTrueRecursively(dirtyFields.heatpump)
  const isError = !!Object.keys(errors.heatpump ?? {}).length

  const [installationId, emc] = useWatch({
    control,
    name: ['id', 'emcDevice'],
  })

  const heatpump = useWatch({
    control,
    name: 'heatpump',
  })
  const { isNew, name, pairingId, deviceId, navigationId } = heatpump ?? {}

  const canPair = !!(installationId && emc?.deviceId && emc?.isPaired && deviceId)

  const handlePairingClick = (): void => {
    if (!canPair) return

    const heatpump = getValues('heatpump')
    if (heatpump === null) {
      logError('Could not open heatpump pairing editor. Heatpump is null')
      return
    }

    openHeatpumpPairingDialog({
      installationId,
      emc,
      heatpump,
      submitPairingId: pairingId => {
        setValue('heatpump.pairingId', pairingId, { shouldDirty: true })
      },
    })
  }

  const displayName = getDeviceDefaultName(name ?? '', 'Heatpump', deviceId ?? '', t)

  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        const device = getValues(`heatpump`)
        if (!device) {
          logError('Heatpump was not found and could not be deleted.')
          return
        }

        Promise.resolve().then(() => {
          deleteIndividualDevice(device, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }

  return (
    <>
      <HideDirtyStateProvider hideDirtyState={!!isNew}>
        <BannerQueue location={BannerLocations.FlowSettingsAdvancedSettingsHvac} sx={{ mt: [2, 0], mb: 2 }} />
        <section id={navigationId}>
          <AccordionItem
            title={<FormSectionTitle header="h2" name={displayName} onClick={handleDelete} isDirty={isDirty} isError={isError} />}
            expanded
          >
            <Box sx={{ p: 2, pt: 0, mb: { xs: 8, sm: 0 } }}>
              <SettingsBox
                title={t('common.name')}
                suffix={<TextField name={'heatpump.name'} maxLength={100} fullWidth={fullWidth} disabled={!canEdit} />}
              />
              <SettingsBox
                title={t('installation.evseEditor.deviceType')}
                suffix={
                  <TextField
                    name={t('installation.evseEditor.deviceType')}
                    value={t('installation.submeterEditor.mediaTypeOptions.modbusTCP')}
                    disabled
                    fullWidth={fullWidth}
                  />
                }
              />
              <SettingsBox
                title={t('installation.deviceStatus.id')}
                suffix={
                  <Box sx={{ display: 'flex', flexDirection: upSm ? 'row' : 'column', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
                    <TextField name={'heatpump.pairingId'} placeholder={t('installation.deviceStatus.id')} disabled fullWidth={fullWidth} />
                    <PairingButton
                      size="large"
                      sx={{ height: '100%' }}
                      onClick={handlePairingClick}
                      disabled={!canPair || !canEdit}
                      icon={pairingId ? 'transfer' : 'link'}
                    >
                      {pairingId ? t('common.replace') : t('common.pair')}
                    </PairingButton>
                  </Box>
                }
              />
              <SettingsBox
                title={t('installation.submeterEditor.nominalPower')}
                suffix={
                  <NumberField
                    name="heatpump.nominalPower"
                    inputProps={{ min: 0, max: 23_000 }}
                    fullWidth={fullWidth}
                    suffix={UnitTypes.watt}
                    disabled={!canEdit}
                  />
                }
              />
            </Box>
          </AccordionItem>
        </section>
      </HideDirtyStateProvider>

      <ScrollRestore />
    </>
  )
}
